import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useDashboard, useMyOrder, useOrg, usePublicData } from '../lib/hooks';
import { errorText } from '../lib/errors';
import { eventEnded, fmtDateTime, inr, isIndianMobile, maskUtr, normalizePhone, ORDER_STATUS, readUtr } from '../lib/format';
import { openCheckout } from '../lib/cashfree';
import { UPI, upiLink } from '../lib/upi';
import { useToast } from '../lib/toast';
import type { OnlinePaymentRef, Order } from '../lib/types';
import { OrderLines, orderSteps, ResidentBar, useSittingMap } from '../components/resident';
import { Chip, ErrorNote, QrCanvas, Spinner, Steps } from '../components/ui';
import { ReceiptButton } from '../components/receipts';

export default function OrderPage() {
  const { code = '' } = useParams();
  const dash = useDashboard();
  const { sittings, settings, events } = usePublicData();
  const onHome = dash.data?.orders.find((o) => o.code === code);
  // Not on the home screen (a past event's order, or an old email link): fetched by its code, with its meals.
  const other = useMyOrder(code, !!dash.data && !dash.isPlaceholderData && !onHome);
  const sittingMap = useSittingMap([...(sittings.data ?? []), ...(other.data?.sittings ?? [])]);
  const order = onHome ?? other.data?.order;

  if (!order) {
    const failed = dash.isError ? dash.error : other.error;
    return (
      <div className="shell">
        <ResidentBar back={{ to: '/', label: 'My passes' }} />
        <div className="app-body">
          {failed ? <p className="err">{errorText(failed)}</p> : <div className="empty"><Spinner /></div>}
        </div>
      </div>
    );
  }
  const eventId = order.event_id ?? sittingMap.get(order.lines[0]?.sitting_id ?? '')?.event_id;
  const event = other.data?.event ?? events.data?.find((e) => e.id === eventId);
  const closed = eventEnded(event?.status);
  const back = closed && event ? { to: `/history/${encodeURIComponent(event.id)}`, label: event.name } : { to: '/', label: 'My passes' };
  const st = ORDER_STATUS[order.status];
  return (
    <div className="shell">
      <ResidentBar back={back} title={order.code} />
      <main className="app-body">
        <div className="ocard-top"><h1 className="display mono" style={{ fontSize: 20 }}>{order.code}</h1><Chip tone={st.tone}>{st.label}</Chip></div>
        <Steps steps={orderSteps(order)} />
        <div className="card">
          <OrderLines order={order} sittings={sittingMap} />
          <div className="ototal"><span>Booked {fmtDateTime(order.created_at)}</span><span>Total <b>{inr(order.total)}</b></span></div>
        </div>
        <ReturnFromCashfree order={order} />
        <StatusBlock order={order} includeAmount={!!settings.data?.upi_include_amount} closed={closed} />
        <PaymentRecord order={order} />
      </main>
    </div>
  );
}

/** Back from Cashfree's checkout (?cf=…): ask the server what happened, then drop the marker from the address. */
function ReturnFromCashfree({ order }: { order: Order }) {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const marker = params.get('cf');
  const [state, setState] = useState<'idle' | 'checking' | 'unpaid' | 'pending' | 'error'>('idle');
  const started = useRef(false);

  useEffect(() => {
    if (!marker || started.current) return;
    started.current = true;
    if (order.status !== 'awaiting_payment') {
      setParams({}, { replace: true });
      return;
    }
    setState('checking');
    api.payCheck(order.code)
      .then(async (r) => {
        await qc.invalidateQueries({ queryKey: ['dashboard'] });
        // Confirmed, or paid but waiting for the committee: the status below says so.
        setState(r.status !== 'awaiting_payment' ? 'idle' : r.pending ? 'pending' : 'unpaid');
        setParams({}, { replace: true });
      })
      .catch(() => setState('error'));
  }, [marker, order.code, order.status, qc, setParams]);

  if (state === 'checking') return <div className="notice"><p><Spinner /> Checking your payment with Cashfree…</p></div>;
  if (state === 'unpaid') {
    return (
      <div className="notice warn">
        <p><b>The payment didn't go through.</b> Nothing was charged, or the money will come back to you. You can try again below.</p>
        <p className="fine">If money did leave your account, wait a few minutes: this page updates once Cashfree tells us. Still not paid after 30 minutes? Tap "Money left my account" under Online payments below.</p>
      </div>
    );
  }
  if (state === 'pending') {
    return (
      <div className="notice warn">
        <p><b>Cashfree is still waiting for your bank.</b> This page updates once the payment completes, usually within a few minutes.</p>
        <p className="fine">Please don't pay again in the meantime.</p>
      </div>
    );
  }
  if (state === 'error') return <div className="notice warn"><p>We couldn't check the payment just now. Refresh this page in a minute.</p></div>;
  return null;
}

/** Cashfree confirms an order paid below its amount only when a checkout offer covered the rest. */
function offerText(order: Order): string {
  const off = order.approved_via === 'online' && order.paid_amount != null ? order.total + order.fee - order.paid_amount : 0;
  return off > 0.009 ? `, after ${inr(off)} off with a Cashfree offer` : '';
}

function StatusBlock({ order, includeAmount, closed }: { order: Order; includeAmount: boolean; closed: boolean }) {
  const [editUtr, setEditUtr] = useState(false);
  if (closed && (order.status === 'awaiting_payment' || order.status === 'sent_back')) {
    return (
      <div className="notice">
        <p><b>This event is over and the order was not paid.</b> There is nothing more to do. If you did pay for it, tell the Food Committee the order code.</p>
      </div>
    );
  }
  switch (order.status) {
    case 'confirmed': {
      const how = order.total === 0 ? 'Your seats are confirmed.'
        : order.pay_mode === 'online' ? `Paid online${order.paid_amount ? `: ${inr(order.paid_amount)}` : ''}${order.fee > 0 ? ` (including ${inr(order.fee)} online payment fee)` : ''}${offerText(order)}.`
        : order.pay_mode === 'cash' ? 'Cash payment approved by the Food Committee.'
        : 'Payment confirmed by the Food Committee.';
      const receipt = order.receipt_no && (
        <>
          <p className="fine">Receipt <span className="mono">{order.receipt_no}</span></p>
          <ReceiptButton load={() => api.myReceipts({ code: order.code })} kind="order" label="Download receipt (PDF)" />
        </>
      );
      // A past event's passes are a record of what was served, under that event.
      if (closed) {
        return (
          <div className="notice ok">
            <p><b>{how}</b></p>
            <p>This event is over. What was served on each pass is on the event's page.</p>
            {order.event_id && <Link className="btn ghost sm" style={{ justifySelf: 'start' }} to={`/history/${encodeURIComponent(order.event_id)}`}>See its passes</Link>}
            {receipt}
          </div>
        );
      }
      return (
        <div className="notice ok">
          <p><b>{how}</b></p>
          <p>Your meal passes are ready in My passes, and we email them to your flat's addresses. Show the QR at the food counter.</p>
          <Link className="btn primary sm" style={{ justifySelf: 'start' }} to="/">See my passes</Link>
          {receipt}
        </div>
      );
    }
    case 'spam':
      return <div className="notice"><p>The Food Committee did not accept this order. If you think this is a mistake, please contact them with the order code.</p></div>;
    case 'cancelled':
      return <div className="notice"><p>This order was cancelled.{order.review_reason ? ` ${order.review_reason}.` : ''}</p></div>;
    case 'awaiting_check':
      if (order.pay_mode === 'online') {
        return (
          <div className="notice warn">
            <p><b>Cashfree received {order.paid_amount != null ? inr(order.paid_amount) : 'a payment'} for this order of {inr(order.total + order.fee)}.</b></p>
            <p>The amounts don't match, so the Food Committee will check it and confirm your passes or contact you. Please don't pay again.</p>
          </div>
        );
      }
      if (order.pay_mode === 'cash') {
        return (
          <div className="notice warn">
            <p><b>Cash received{order.cash_recorded_at ? ` ${fmtDateTime(order.cash_recorded_at)}` : ''}.</b></p>
            <p>A second Food Committee member approves cash payments. Your passes unlock as soon as they do.</p>
          </div>
        );
      }
      return (
        <div className="stack">
          <div className="notice warn">
            <p><b>Waiting for the Food Committee to match your payment.</b></p>
            <p>UTR {maskUtr(order.utr, order.legacy_utr_last4)} · sent {order.utr_submitted_at ? fmtDateTime(order.utr_submitted_at) : ''}. It is matched against the bank statement; your passes unlock as soon as it is confirmed.</p>
          </div>
          {editUtr ? <UtrForm order={order} onDone={() => setEditUtr(false)} /> : (
            <button className="link-btn" onClick={() => setEditUtr(true)}>Entered the wrong UTR? Correct it</button>
          )}
        </div>
      );
    case 'sent_back':
      return (
        <div className="stack">
          <div className="notice bad">
            <p><b>The Food Committee couldn't match your payment.</b></p>
            {order.review_reason && <p>{order.review_reason}</p>}
            <p>If you already paid, send the full 12-digit UTR below. Don't pay again.</p>
          </div>
          <UtrForm order={order} />
        </div>
      );
    default:
      return <PayChooser order={order} includeAmount={includeAmount} />;
  }
}

type Method = 'online' | 'upi' | 'cash';

/** How to pay an unpaid order: online through Cashfree, UPI to the committee with the UTR, or cash at the help desk. */
function PayChooser({ order, includeAmount }: { order: Order; includeAmount: boolean }) {
  const quote = useQuery({ queryKey: ['pay-quote', order.code], queryFn: () => api.payQuote(order.code) });
  const [method, setMethod] = useState<Method | null>(null);
  const online = !!quote.data?.online;
  const cashOk = quote.data?.cash !== false;
  const chosen: Method = method ?? (order.pay_mode === 'cash' && cashOk ? 'cash' : online ? 'online' : 'upi');

  if (quote.isPending) return <div className="empty"><Spinner /></div>;
  const options: { id: Method; title: string; hint: string }[] = [
    ...(online ? [{ id: 'online' as const, title: 'Pay online', hint: 'Any UPI app, card or netbanking. Your passes are confirmed at once.' }] : []),
    { id: 'upi', title: 'Pay by UPI and send the UTR', hint: "Pay the committee's UPI ID, then send the 12-digit UTR. Confirmed once the committee matches it." },
    ...(cashOk ? [{ id: 'cash' as const, title: 'Pay cash at the help desk', hint: 'Hand the cash to the Food Committee. Confirmed once they approve it.' }] : []),
  ];
  return (
    <div className="stack">
      {order.online_pending && <OnlinePending code={order.code} />}
      <fieldset className="card pay-methods">
        <legend className="eyebrow">Step 2 · How would you like to pay?</legend>
        {options.map((o) => (
          <label key={o.id} className={`pay-method${chosen === o.id ? ' is-on' : ''}`}>
            <input type="radio" name="pay-method" checked={chosen === o.id} onChange={() => setMethod(o.id)} />
            <span><b>{o.title}</b><small>{o.hint}</small></span>
          </label>
        ))}
      </fieldset>
      {chosen === 'online' && quote.data && <OnlinePay order={order} fee={quote.data.fee} amount={quote.data.amount} phone={quote.data.phone} />}
      {chosen === 'upi' && (
        <>
          <PayCard order={order} includeAmount={includeAmount} />
          <UtrForm order={order} />
        </>
      )}
      {chosen === 'cash' && <CashPay order={order} />}
      <CancelOrder code={order.code} />
    </div>
  );
}

function OnlinePending({ code }: { code: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <div className="notice">
      <p><b>An online payment for this order was started.</b> If you paid, it shows here within a few minutes.</p>
      <button className="btn ghost sm" style={{ justifySelf: 'start' }} disabled={busy} onClick={async () => {
        setBusy(true);
        try {
          const r = await api.payCheck(code);
          await qc.invalidateQueries({ queryKey: ['dashboard'] });
          toast(r.status === 'confirmed' ? 'Payment received. Your passes are ready.'
            : r.status === 'awaiting_check' ? 'Payment received. The Food Committee will check it.'
            : r.pending ? 'Cashfree is still waiting for your bank.' : 'No completed payment yet.');
        } catch (e) {
          toast(errorText(e), { tone: 'bad' });
        } finally {
          setBusy(false);
        }
      }}>{busy ? 'Checking…' : 'Check payment status'}</button>
    </div>
  );
}

function OnlinePay({ order, fee, amount, phone }: { order: Order; fee: number; amount: number; phone: string | null }) {
  const payee = useOrg().data?.payee_name;
  const [mobile, setMobile] = useState(phone ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Step 2: the payment is set up at Cashfree; the family notes its reference before going there.
  const [ready, setReady] = useState<{ session: string; mode: 'sandbox' | 'production'; ref: string } | null>(null);
  const needPhone = !phone;

  async function start() {
    setError(null);
    const m = normalizePhone(mobile);
    if (needPhone && !isIndianMobile(m)) return setError(errorText('phone_required'));
    setBusy(true);
    try {
      const s = await api.payStart(order.code, needPhone ? m : undefined);
      setReady({ session: s.session_id, mode: s.mode, ref: s.ref });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function go() {
    if (!ready) return;
    setBusy(true);
    try {
      await openCheckout(ready.session, ready.mode);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  }

  if (ready) {
    return (
      <section className="card payref">
        <p className="eyebrow">Your payment reference</p>
        <p className="payref-code mono">{ready.ref}</p>
        <CopyButton text={ready.ref} label="Copy reference" />
        <ul className="payref-list">
          <li>Take a screenshot or note it down before you pay.</li>
          <li>Pay <b>{inr(amount)}</b> on Cashfree's page; you come back here when it's done.</li>
          <li>If money leaves your account but this order doesn't say <b>Confirmed</b> within 30 minutes, tap <b>Money left my account</b> on this page and quote this reference. Cashfree returns money for payments that fail, usually within 5–7 working days.</li>
        </ul>
        <ErrorNote error={error} />
        <button className="btn primary block lg" disabled={busy} onClick={() => void go()}>{busy ? 'Opening Cashfree…' : `I've noted it · Pay ${inr(amount)}`}</button>
        <button className="btn ghost sm quiet" style={{ justifySelf: 'center' }} disabled={busy} onClick={() => setReady(null)}>Back</button>
      </section>
    );
  }

  return (
    <section className="card">
      <p className="pay-amt">{inr(amount)}</p>
      {fee > 0 && <p className="fine center">{inr(order.total)} for the meals + {inr(fee)} online payment fee. Paying by UPI with the UTR, or in cash, has no fee.</p>}
      {needPhone && (
        <div>
          <label className="fld-l" htmlFor="pay-mobile">Mobile number</label>
          <input id="pay-mobile" className="fld" inputMode="tel" autoComplete="tel" placeholder="10-digit mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} />
          <p className="fine">Cashfree, the payment service, needs it for the payment.</p>
        </div>
      )}
      <ErrorNote error={error} />
      <button className="btn primary block lg" disabled={busy} onClick={() => void start()}>{busy ? 'Setting up the payment…' : `Pay ${inr(amount)}`}</button>
      <p className="fine">You go to Cashfree's secure page and come back here. The money goes straight to {payee ? `the ${payee}'s` : "the committee's"} bank account.</p>
    </section>
  );
}

function CashPay({ order }: { order: Order }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (order.pay_mode !== 'cash') {
    return (
      <section className="card">
        <p>Pay <b>{inr(order.total)}</b> in cash to the Food Committee at the help desk. Your passes unlock after the committee approves the payment.</p>
        <button className="btn primary block" disabled={busy} onClick={async () => {
          setBusy(true);
          try {
            await api.choosePayMode(order.code, 'cash');
            await qc.invalidateQueries({ queryKey: ['dashboard'] });
            toast('Noted: you will pay in cash');
          } catch (e) {
            toast(errorText(e), { tone: 'bad' });
          } finally {
            setBusy(false);
          }
        }}>{busy ? 'Saving…' : "I'll pay cash"}</button>
      </section>
    );
  }
  return (
    <section className="card">
      <p className="eyebrow">Paying cash</p>
      <p className="pay-amt">{inr(order.total)}</p>
      <p>Hand it to the Food Committee at the help desk and give the order code <b className="mono">{order.code}</b>. Your passes unlock once a committee member approves it.</p>
    </section>
  );
}

function PayCard({ order, includeAmount }: { order: Order; includeAmount: boolean }) {
  const link = upiLink(order.total, order.code, includeAmount);
  const toast = useToast();
  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(`${what} copied`);
    } catch {
      toast(`Copy failed. ${what}: ${text}`);
    }
  };
  const mobile = /android|iphone|ipad/i.test(navigator.userAgent);
  return (
    <section className="card">
      <p className="eyebrow">Pay by UPI</p>
      <p className="pay-amt">{inr(order.total)}</p>
      <div className="upi-qr"><QrCanvas value={link} /></div>
      <p className="fine center">Scan with GPay, PhonePe, Paytm or any UPI app.{includeAmount ? '' : ` Enter ${inr(order.total)} as the amount.`}</p>
      {mobile && <a className="btn ghost block" href={link}>Open a UPI app on this phone</a>}
      <div className="copy-row"><span>UPI ID <b className="mono">{UPI.vpa}</b></span><button className="btn ghost sm" onClick={() => void copy(UPI.vpa, 'UPI ID')}>Copy</button></div>
      <div className="copy-row"><span>Amount <b>{inr(order.total)}</b></span><button className="btn ghost sm" onClick={() => void copy(String(order.total), 'Amount')}>Copy</button></div>
      <p className="fine">Payee: {UPI.payee}. Add <b className="mono">{order.code}</b> in the payment note if your app allows it.</p>
    </section>
  );
}

function UtrForm({ order, onDone }: { order: Order; onDone?: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [utr, setUtr] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 4 digits typed: ask once whether they really are the last 4 of the UTR.
  const [askLast4, setAskLast4] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  function take(text: string) {
    setError(null);
    setAskLast4(false);
    const r = readUtr(text);
    if (r.txnId) {
      setError('That looks like a transaction ID, not the UTR. Copy the 12-digit UTR or UPI Ref. No. instead.');
      return;
    }
    setUtr(r.utr);
  }

  async function paste() {
    try {
      take(await navigator.clipboard.readText());
    } catch {
      input.current?.focus();
      toast('Touch and hold the box, then choose Paste');
    }
  }

  async function send(value: string) {
    setBusy(true);
    try {
      await api.submitUtr(order.code, value);
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast(value.length === 4 ? 'Last 4 digits sent to the Food Committee' : 'UTR sent to the Food Committee');
      setUtr('');
      setAskLast4(false);
      onDone?.();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  function submit() {
    setError(null);
    if (/^\d{12}$/.test(utr)) return void send(utr);
    if (/^\d{4}$/.test(utr)) return setAskLast4(true);
    setError(errorText('utr_format'));
  }

  return (
    <form className="card" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <p className="eyebrow">Then send your UTR</p>
      <div>
        <label className="fld-l" htmlFor="utr">UTR (UPI reference number)</label>
        <div className="utr-row">
          <input id="utr" ref={input} className="fld utr-in" inputMode="numeric" autoComplete="off" placeholder="12 digits, e.g. 418322973456"
            value={utr} onChange={(e) => take(e.target.value)} />
          {'clipboard' in navigator && <button type="button" className="btn ghost" onClick={() => void paste()}>Paste</button>}
        </div>
        <p className="fine">All 12 digits, or just the last 4.</p>
      </div>
      <div className="help-list">
        <span>Copy it straight from your UPI app and paste it here: open the payment's details.</span>
        <span><b>GPay</b> · "UPI transaction ID"</span>
        <span><b>PhonePe</b> · "UTR" (not the Transaction ID that starts with T)</span>
        <span><b>Paytm</b> · "UPI Ref. No."</span>
        <span><b>Bank SMS</b> · the 12-digit "UPI Ref No." works too</span>
      </div>
      <ErrorNote error={error} />
      {askLast4 ? (
        <div className="notice warn utr-ask" role="alert">
          <p><b>Are {utr} the last 4 digits of your UTR?</b> The full 12-digit number helps the Food Committee confirm your payment faster.</p>
          <div className="row">
            <button type="button" className="btn primary sm" disabled={busy} onClick={() => void send(utr)}>{busy ? 'Sending…' : 'Yes, send the last 4'}</button>
            <button type="button" className="btn ghost sm" disabled={busy} onClick={() => { setAskLast4(false); input.current?.focus(); }}>I'll enter all 12</button>
          </div>
        </div>
      ) : (
        <button className="btn primary block" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send UTR'}</button>
      )}
    </form>
  );
}

function CancelOrder({ code }: { code: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!confirming) {
    return <button className="link-btn" style={{ color: 'var(--ink-soft)' }} onClick={() => setConfirming(true)}>Cancel this order</button>;
  }
  return (
    <div className="notice">
      <p>Cancel {code}? Only do this if you haven't paid.</p>
      <div className="row">
        <button className="btn danger sm" disabled={busy} onClick={async () => {
          setBusy(true);
          try {
            await api.cancelMyOrder(code);
            await qc.invalidateQueries({ queryKey: ['dashboard'] });
            toast('Order cancelled');
          } catch (e) {
            toast(errorText(e), { tone: 'bad' });
          } finally {
            setBusy(false);
            setConfirming(false);
          }
        }}>Yes, cancel order</button>
        <button className="btn ghost sm" onClick={() => setConfirming(false)}>Keep it</button>
      </div>
    </div>
  );
}

const ATTEMPT: Record<string, string> = { SUCCESS: 'Paid', FAILED: 'Failed', USER_DROPPED: 'Left on the payment page', PENDING: 'Waiting for the bank', CANCELLED: 'Cancelled', VOID: 'Cancelled' };
const attemptText = (p: OnlinePaymentRef) =>
  p.status === 'paid' ? 'Paid' : p.last_status ? (ATTEMPT[p.last_status] ?? p.last_status.toLowerCase()) : p.status === 'active' ? 'Started, not finished' : 'Not finished';

/**
 * The family's own record of the order's online payments, each by its reference, and "Money left my account" for when
 * Cashfree took money but the order still isn't paid: the Food Committee sees it in Payments and checks with Cashfree.
 */
function PaymentRecord({ order }: { order: Order }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const payments = order.payments ?? [];
  const unpaid = ['awaiting_payment', 'awaiting_check', 'sent_back'].includes(order.status);
  if (!payments.length && !order.payment_issue_at) return null;

  async function report() {
    setBusy(true);
    try {
      await api.reportPaymentIssue(order.code, note.trim());
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
      setOpen(false);
      toast('Sent to the Food Committee');
    } catch (e) {
      toast(errorText(e), { tone: 'bad' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack">
      <p className="eyebrow">Online payments</p>
      <ul className="payrec">
        {payments.map((p) => (
          <li key={p.ref}>
            <span className="mono">{p.ref}</span>
            <span className="fine">{fmtDateTime(p.at)} · {inr(p.amount)} · <b>{attemptText(p)}</b></span>
          </li>
        ))}
      </ul>
      {unpaid && order.payment_issue_at ? (
        <div className="notice warn">
          <p><b>You told the Food Committee about a payment problem</b> ({fmtDateTime(order.payment_issue_at)}){order.payment_issue_note ? `: "${order.payment_issue_note}"` : ''}.</p>
          <p className="fine">They check with Cashfree and contact you. Please don't pay again unless they ask.</p>
        </div>
      ) : unpaid && payments.length > 0 && (open ? (
        <div className="stack">
          <label className="fld-l" htmlFor="issue-note">What happened? (optional)</label>
          <textarea id="issue-note" className="fld" rows={2} maxLength={300} placeholder="₹740 left my account at 6:10 PM from GPay"
            value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="row">
            <button className="btn primary sm" disabled={busy} onClick={() => void report()}>{busy ? 'Sending…' : 'Tell the Food Committee'}</button>
            <button className="btn ghost sm quiet" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn ghost sm" style={{ justifySelf: 'start' }} onClick={() => setOpen(true)}>Money left my account</button>
      ))}
    </section>
  );
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const toast = useToast();
  return (
    <button type="button" className="btn ghost sm" style={{ justifySelf: 'center' }} onClick={async () => {
      try {
        await navigator.clipboard.writeText(text);
        toast('Copied');
      } catch {
        toast(`Copy failed. Note it down: ${text}`);
      }
    }}>{label}</button>
  );
}
